import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { IActivo } from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { IParamEventoUsuario } from '../../../../standalone/config-evento-usuario/crear-editar-config-evento-usuario-dialog/crear-editar-config-evento-usuario-dialog.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { isModoDesactivadoActivo } from '../../../../../auxiliares/modo-desactivado.util';
import { CrearEditarActivoComponent } from '../crear-editar-activo/crear-editar-activo.component';

@Component({
  selector: 'app-detalles-activo',
  templateUrl: './detalles-activo.component.html',
  styleUrl: '../../../../../componentes/detalle/detalle-pagina.scss',
  standalone: false,
})
export class DetallesActivoComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lista2 = inject(Lista2Service);
  private formularios = inject(FormularioDialogService);
  private helper = inject(HelperService);

  // El mismo permiso que pide el listado para editar
  public puedeEditar = this.helper.tienePermiso('Activos - Editar');

  public selectedTab = 0;
  public idActivo: string | null = null;
  public activo?: IActivo;
  public loading: boolean = true;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  public params?: IParamEventoUsuario;

  /** Desactivado como lo muestra el listado: el flag y la fecha de fin. */
  get desactivado(): boolean {
    return isModoDesactivadoActivo(this.activo?.modoDesactivado);
  }

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): string[] {
    return [
      'info',
      'recorridos',
      'eventos',
      'codigos',
      'eventosPersonalizados',
      'notas',
      'contactos',
      'servicioTecnico',
      'asignaciones',
    ];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  private async listar(): Promise<void> {
    // Si el activo no existe (se borró, no hay acceso) el getById falla: la
    // promesa no se resolvía y la página quedaba con el spinner para siempre.
    try {
      this.activo = await firstValueFrom(this.lista2.activo(this.idActivo));
    } catch {
      this.activo = undefined;
    }
  }

  public editar(): void {
    const dialogo = this.formularios.abrir(CrearEditarActivoComponent, {
      tamano: 'mediano',
      data: {
        edit: this.activo,
        tipo: 'Normal',
      },
    });
    // Info ya no pide el activo cada 30 s: se recarga al guardar.
    dialogo.afterClosed().subscribe(async (guardado) => {
      if (!guardado) return;
      await this.listar();
    });
  }

  public botonCrearMostrar(): boolean {
    return HelperService.getConfigCliente().moduloActivos?.crearDispositivos
      ? true
      : false;
  }

  /**
   * Carga el activo del id de la ruta. Se llama cada vez que cambia el id:
   * Angular reutiliza el componente al ir de un activo a otro.
   */
  private async cargar(id: string | null): Promise<void> {
    this.loading = true;
    this.activo = undefined;
    this.idActivo = id;
    await this.listar();

    // Los parametros para el componente config evento usuario
    this.params = {
      tipoEntidad: 'Activo',
      agrupacion: 'Entidad',
      idEntidad: this.idActivo,
      botonCrear:
        HelperService.getConfigCliente().moduloActivos?.crearDispositivos,
      activo: this.activo,
      crear: 'Dialog',
    };
    this.loading = false;
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((params) => {
      const indice = params['tab'] ? this.indicePestana(params['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
